import { useMemo } from 'react'
import { formatClock12, formatDuration } from '@trackmind/core'
import { CheckCircle, Clock, FolderOpen, Repeat, Calendar } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { EmptyState, emptyStateConfigs } from '@/components/EmptyState'
import type { Schedule, Task, ScheduledTask, Project } from '@/types'
import { cn } from '@/lib/utils'

interface TodayByProjectProps {
  schedule: Schedule | null
  tasks: Task[]
  projects: Project[]
  currentDate: string
  /** Mirror of the All/Tasks/Habits toggle so this layout stays in sync. */
  scheduleFilter?: 'all' | 'tasks' | 'habits'
  isTaskCompleted: (id: string) => boolean
  onCompleteTask: (id: string, forDate?: string) => void
  /** Untick — the same control must work both ways everywhere it appears. */
  onUncompleteTask?: (id: string, forDate?: string) => void
  onTaskClick: (scheduled: ScheduledTask) => void
}

interface ProjectGroup {
  project: Project | null  // null means "No project"
  scheduled: ScheduledTask[]
  completedCount: number
  totalCount: number
}

export function TodayByProject({
  schedule,
  tasks,
  projects,
  currentDate,
  scheduleFilter = 'all',
  isTaskCompleted,
  onCompleteTask,
  onUncompleteTask,
  onTaskClick,
}: TodayByProjectProps) {
  const getTaskById = (id: string) => tasks.find((t) => t.id === id)
  const getProjectById = (id: string) => projects.find((p) => p.id === id)

  const groups = useMemo<ProjectGroup[]>(() => {
    const scheduled = (schedule?.scheduled_tasks || []).filter((st) => {
      if (st.is_buffer) return false
      if (scheduleFilter === 'all') return true
      const task = tasks.find(t => t.id === st.task_id)
      if (!task) return false
      return scheduleFilter === 'habits' ? !!task.is_habit : !task.is_habit
    })
    const byProject = new Map<string, ScheduledTask[]>()

    for (const st of scheduled) {
      const task = getTaskById(st.task_id)
      const key = task?.project_id || '__none__'
      const arr = byProject.get(key) || []
      arr.push(st)
      byProject.set(key, arr)
    }

    const result: ProjectGroup[] = []
    for (const [pid, list] of byProject.entries()) {
      const project = pid === '__none__' ? null : getProjectById(pid) || null
      const completedCount = list.filter((st) => isTaskCompleted(st.task_id)).length
      result.push({
        project,
        scheduled: list.sort(
          (a, b) => a.start_time.localeCompare(b.start_time)
        ),
        completedCount,
        totalCount: list.length,
      })
    }

    // Sort: projects with remaining work first, then alphabetical
    result.sort((a, b) => {
      const aRemaining = a.totalCount - a.completedCount
      const bRemaining = b.totalCount - b.completedCount
      if (aRemaining !== bRemaining) return bRemaining - aRemaining
      const aName = a.project?.name || 'No project'
      const bName = b.project?.name || 'No project'
      return aName.localeCompare(bName)
    })

    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule, tasks, projects, scheduleFilter])

  if (groups.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <EmptyState
            icon={Calendar}
            title={emptyStateConfigs.schedule.title}
            description={emptyStateConfigs.schedule.description}
            compact
          />
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {groups.map((group) => {
        const name = group.project?.name || 'No project'
        const color = group.project?.color
        const remaining = group.totalCount - group.completedCount

        return (
          <Card
            key={group.project?.id || '__none__'}
            className="flex flex-col"
            style={
              color
                ? { borderTopWidth: '3px', borderTopColor: color }
                : undefined
            }
          >
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 truncate">
                  <FolderOpen
                    className="h-4 w-4 flex-shrink-0"
                    style={color ? { color } : undefined}
                  />
                  <span className="truncate">{name}</span>
                </CardTitle>
                <Badge variant="secondary" className="text-xs flex-shrink-0">
                  {group.completedCount}/{group.totalCount}
                </Badge>
              </div>
              {remaining === 0 && group.totalCount > 0 && (
                <p className="text-xs text-green-500 flex items-center gap-1 mt-1">
                  <CheckCircle className="h-3 w-3" />
                  All done for today
                </p>
              )}
            </CardHeader>
            <CardContent className="pt-0 space-y-2 flex-1">
              {group.scheduled.map((st) => {
                const task = getTaskById(st.task_id)
                const completed = isTaskCompleted(st.task_id)

                return (
                  <div
                    key={st.task_id + st.start_time}
                    className={cn(
                      'group flex items-start gap-2 p-2 rounded-md border transition-colors cursor-pointer hover:bg-accent/50',
                      completed && 'opacity-60'
                    )}
                    onClick={() => onTaskClick(st)}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        // Both directions. The one-way version made this the
                        // single place in the app where a tick was a trap.
                        const forDate = task?.is_habit ? currentDate : undefined
                        if (completed) onUncompleteTask?.(st.task_id, forDate)
                        else onCompleteTask(st.task_id, forDate)
                      }}
                      className={cn(
                        'mt-0.5 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors',
                        completed
                          ? 'bg-green-500 border-green-500 text-white'
                          : 'border-muted-foreground hover:border-primary hover:bg-primary/10'
                      )}
                      title={completed ? 'Completed — click to undo' : 'Mark complete'}
                    >
                      {completed && <CheckCircle className="h-3 w-3" />}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            'text-sm font-medium truncate',
                            completed && 'line-through text-muted-foreground'
                          )}
                        >
                          {task?.title || 'Unknown task'}
                        </span>
                        {task?.is_habit && (
                          <Badge
                            variant="secondary"
                            className="text-[10px] h-4 px-1 flex-shrink-0"
                          >
                            <Repeat className="h-2.5 w-2.5 mr-0.5" />
                            Habit
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5">
                        <span className="flex items-center gap-0.5 font-mono">
                          <Clock className="h-3 w-3" />
                          {formatClock12(st.start_time)}
                        </span>
                        <span>·</span>
                        <span>{formatDuration(st.estimated_minutes)}</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}

export default TodayByProject
